import { useEffect, useState, useCallback } from "react";
import { Shield, RefreshCw, Link2, AlertTriangle, Clock, ClipboardCheck, Users, Building2, Mic2, Activity, ArrowRight, Send, PackageCheck, RotateCcw } from "lucide-react";
import { http, fmtRupee, fmtDateTime, fmtDate, timeLeft } from "../api";
import OrderDetailModal, { StageBadge } from "../components/OrderDetailModal";

const REFRESH_MS = 30000;

function AttentionPanel({ attention, onOpenOrder, setPage }) {
  const items = [
    { key: "overdue_ack", icon: Clock, tone: "danger", label: "12-hr acknowledgement overdue", list: attention.overdue_ack },
    { key: "deadline_requests", icon: AlertTriangle, tone: "danger", label: "Deadline extension requests", list: attention.deadline_requests },
    { key: "revision_requested", icon: RotateCcw, tone: "warn", label: "Revisions requested by client", list: attention.revision_requested },
    { key: "awaiting_client_approval", icon: PackageCheck, tone: "warn", label: "Delivered · awaiting client approval", list: attention.awaiting_client_approval },
  ];
  const counts = [
    { key: "pending_samples", label: "Samples to review", value: attention.pending_samples, page: "samples", icon: ClipboardCheck },
    { key: "pending_artists", label: "Artists to approve", value: attention.pending_artists, page: "artists", icon: Users },
  ];
  const total = items.reduce((n, i) => n + i.list.length, 0) + counts.reduce((n, c) => n + c.value, 0);
  return (
    <div className="card" data-testid="attention-panel">
      <div className="card-head">
        <h2><AlertTriangle size={15} style={{ display: "inline", marginRight: 6, color: total ? "var(--danger)" : "var(--success)" }} />Needs your attention</h2>
        <span className={`badge ${total ? "danger" : "success"}`} data-testid="attention-count">{total} items</span>
      </div>
      {total === 0 && <p style={{ fontSize: 13, color: "var(--muted)" }}>All clear. Nothing is waiting on you right now.</p>}
      <div className="attention-list">
        {counts.filter(c => c.value > 0).map(c => (
          <button key={c.key} className="attention-row warn" onClick={() => setPage(c.page)} data-testid={`attention-${c.key}`}>
            <c.icon size={15} /><span>{c.label}</span><strong>{c.value}</strong><ArrowRight size={14} />
          </button>
        ))}
        {items.filter(i => i.list.length).map(i => (
          <div key={i.key}>
            <div className={`attention-row ${i.tone}`} style={{ cursor: "default" }} data-testid={`attention-${i.key}`}>
              <i.icon size={15} /><span>{i.label}</span><strong>{i.list.length}</strong>
            </div>
            {i.list.slice(0, 5).map(o => (
              <button key={o.id} className="attention-sub" onClick={() => onOpenOrder(o.id)} data-testid={`attention-order-${o.id}`}>
                <span>{o.order_code}</span> {o.client_company || o.client_name} → {o.artist_name} · {o.project_title}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersBoard({ orders, onOpenOrder }) {
  return (
    <div className="card" data-testid="active-orders-board">
      <div className="card-head"><h2>Active projects · client ↔ artist</h2><span className="badge">{orders.length} live</span></div>
      {orders.length === 0 && <p style={{ fontSize: 13, color: "var(--muted)" }}>No active projects right now.</p>}
      <div className="board">
        {orders.map(o => {
          const ack = o.status === "PENDING_ARTIST_ACKNOWLEDGEMENT" ? timeLeft(o.ack_deadline) : null;
          const due = timeLeft(o.deadline);
          return (
            <button key={o.id} className={`board-card ${o.deadline_revision_status === "REQUESTED" || ack?.tone === "danger" ? "alert" : ""}`} onClick={() => onOpenOrder(o.id)} data-testid={`board-order-${o.id}`}>
              <div className="board-top"><span className="board-code">{o.order_code}</span><StageBadge status={o.status} /></div>
              <div className="board-title">{o.project_title}</div>
              <div className="board-parties">
                <span><Building2 size={12} /> {o.client_company || o.client_name}</span>
                <ArrowRight size={12} />
                <span><Mic2 size={12} /> {o.artist_name}</span>
              </div>
              <div className="board-foot">
                <span>{fmtRupee(o.client_amount)} · {o.estimated_hours}h</span>
                {ack ? <span className={`t-${ack.tone}`}>Ack {ack.text}</span> : <span className={`t-${due?.tone}`}>Due {fmtDate(o.deadline)}</span>}
              </div>
              {o.deadline_revision_status === "REQUESTED" && <div className="board-alert"><AlertTriangle size={11} /> Deadline extension requested</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MonthApprovedProjects({ orders, onOpenOrder }) {
  return (
    <div className="card" data-testid="month-approved-projects">
      <div className="card-head"><h2>इस महीने client-approved projects</h2><span className="badge success" data-testid="month-approved-count">{orders.length} approved</span></div>
      {orders.length === 0 && <p style={{ fontSize: 13, color: "var(--muted)" }}>इस महीने अभी कोई project client ने approve नहीं किया है।</p>}
      <div className="list-rows">
        {orders.map(o => (
          <button key={o.id} className="list-row" onClick={() => onOpenOrder(o.id)} data-testid={`month-approved-order-${o.id}`}>
            <div className="list-row-info"><strong>{o.project_title}</strong><p>{o.order_code} · {o.client_company || o.client_name} → {o.artist_name}</p><small>{o.duration_hours}h final duration · Approved {fmtDate(o.completed_at)}</small></div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12.5, textAlign: "right" }}>
              <div data-testid={`month-client-payable-${o.id}`}><small>Client payable</small><strong>{fmtRupee(o.client_amount)}</strong></div>
              <div data-testid={`month-artist-payable-${o.id}`}><small>Artist payable</small><strong>{fmtRupee(o.artist_payout)}</strong></div>
              <div data-testid={`month-admin-margin-${o.id}`}><small>Your margin</small><strong style={{ color: "var(--success)" }}>{fmtRupee(o.admin_commission)}</strong></div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Feed({ feed, onOpenOrder }) {
  return (
    <div className="card" data-testid="activity-feed">
      <div className="card-head"><h2><Activity size={15} style={{ display: "inline", marginRight: 6 }} />Live activity</h2></div>
      <ul className="feed">
        {feed.map((f, i) => (
          <li key={i} className={f.type} data-testid="feed-item">
            <span className="feed-dot" />
            <div>
              <div className="feed-text">
                {f.type === "order" ? (
                  <><strong>{f.client_company || f.client_name}</strong> · <strong>{f.artist_name}</strong> — {f.event} <em>({f.project_title})</em></>
                ) : (
                  <><strong>{f.artist_name}</strong> — {f.event}</>
                )}
              </div>
              <small>{f.actor} · {fmtDateTime(f.created_at)}{f.order_code ? <> · <button className="link" onClick={() => onOpenOrder(f.order_id)}>{f.order_code}</button></> : null}</small>
            </div>
          </li>
        ))}
        {feed.length === 0 && <li style={{ color: "var(--muted)", fontSize: 13 }}>No activity yet.</li>}
      </ul>
    </div>
  );
}

export default function AdminOverview({ setPage }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [copied, setCopied] = useState(false);
  const load = useCallback(() => {
    setSpinning(true);
    return http.get("/admin/dashboard").then(r => setData(r.data)).catch(() => {}).finally(() => setSpinning(false));
  }, []);
  useEffect(() => { load(); const i = setInterval(load, REFRESH_MS); return () => clearInterval(i); }, [load]);

  if (!data) return <div className="loading">Loading dashboard…</div>;
  const s = data.stats;
  const cards = [
    { label: "Active projects", value: s.active_orders, tone: "" },
    { label: "Completed projects", value: s.completed_orders, tone: "success" },
    { label: "Client billed (month)", value: fmtRupee(s.month_client_billed), tone: "success" },
    { label: "Artist payout (month)", value: fmtRupee(s.month_artist_payout), tone: "" },
    { label: "Your commission (month)", value: fmtRupee(s.month_commission), tone: "success" },
    { label: "Approved voices", value: s.approved_voices, tone: "" },
    { label: "Clients", value: s.clients, tone: "" },
  ];
  return (
    <>
      <div className="page-head">
        <div><h1>Owner dashboard</h1><p>What's happening between clients and artists — live.</p></div>
        <div className="page-head-side">
          <button className="ghost" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/apply`); setCopied(true); setTimeout(() => setCopied(false), 2000); }} data-testid="copy-apply-link"><Link2 size={13} /> {copied ? "Copied!" : "Copy artist form link"}</button>
          <button className="ghost" onClick={() => setPage("settings")} data-testid="goto-telegram"><Send size={13} /> Telegram alerts</button>
          <button className="ghost" onClick={load} data-testid="refresh-dashboard"><RefreshCw size={13} className={spinning ? "spin" : ""} /> Updated {fmtDateTime(data.generated_at)}</button>
          <span className="badge success"><Shield size={12} /> Admin</span>
        </div>
      </div>
      <div className="stats">
        {cards.map(c => (
          <div key={c.label} className={`stat-card ${c.tone}`} data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span>{c.label}</span><strong>{c.value ?? 0}</strong><small>Auto-refreshes every 30s</small>
          </div>
        ))}
      </div>
      <div className="dash-grid">
        <div className="dash-main">
          <AttentionPanel attention={data.attention} onOpenOrder={setOpenId} setPage={setPage} />
          <OrdersBoard orders={data.active_orders} onOpenOrder={setOpenId} />
          <MonthApprovedProjects orders={data.month_approved_projects || []} onOpenOrder={setOpenId} />
        </div>
        <Feed feed={data.feed} onOpenOrder={setOpenId} />
      </div>
      {openId && <OrderDetailModal orderId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </>
  );
}
